import * as React from "react";
import styled, { css } from "styled-components";
import throttle from "lodash/throttle";

import { ScreenSize, isLarger } from "../../../common/utils/media";
import { colors, media } from "../../../common/utils/style";
import SectionAsideNavItem from "../molecules/SectionAsideNavItem";
import { NavElementProps } from "../../../common/components/SectionNavigator";
import WithScreenSize, { ScreenSizeProps } from "../../../common/components/WithScreenSize";

type OwnProps = NavElementProps & {
  navTitle: string | React.ReactNode;
  activeColor?: string | null;
};

type Props = OwnProps & ScreenSizeProps;

interface State {
  positioned: boolean;
  fixed: boolean;
  top: number | null;
  bottom: number | null;
}

class SectionAsideNav extends React.Component<Props, State> {
  public static defaultProps: Partial<Props> = {
    activeColor: colors.lightgray1,
  };

  private base: HTMLElement | null = null;
  private listeningScroll: boolean;
  private onScrollThrottled: () => void;

  constructor(props: Props) {
    super(props);
    this.listeningScroll = false;
    this.state = {
      positioned: false,
      fixed: false,
      top: 0,
      bottom: null,
    };
    this.onScrollThrottled = throttle(this.onScroll, 50);
  }

  public componentDidMount() {
    this.listenScrollIfWideView(this.props.screenSize);
  }

  public componentWillReceiveProps(nextProps: Props) {
    if (isLarger(nextProps.screenSize, this.props.screenSize)) {
      this.listenScrollIfWideView(nextProps.screenSize!);
    }
    if (nextProps.wrapperRef && nextProps.wrapperRef !== this.props.wrapperRef) {
      this.updatePositionState(nextProps.wrapperRef);
    }
  }

  public componentWillUnmount() {
    window.removeEventListener("scroll", this.onScrollThrottled);
  }

  public listenScrollIfWideView(screenSize: ScreenSize) {
    if (this.listeningScroll) {
      return;
    }
    if (isLarger(screenSize, "large", true)) {
      window.addEventListener("scroll", this.onScrollThrottled);
      this.listeningScroll = true;
    }
  }

  public onScroll = () => {
    this.updatePositionState();
  };

  public updatePositionState(wrapperRef = this.props.wrapperRef) {
    if (!wrapperRef) {
      return;
    }
    this.setState({ ...this.calcPositionState(wrapperRef), positioned: true });
  }

  public calcPositionState(wrapperRef: HTMLElement): State {
    if (!this.base) {
      return this.state;
    }
    const windowHeight = window.innerHeight;
    const baseHeight = this.base.clientHeight;
    const wrapperRect = wrapperRef.getBoundingClientRect();
    if (wrapperRect.top > windowHeight / 2 - baseHeight / 2) {
      return { ...this.state, fixed: false, top: 0, bottom: null };
    } else if (wrapperRect.bottom < windowHeight / 2 + baseHeight / 2) {
      return { ...this.state, fixed: false, top: null, bottom: 0 };
    }
    return { ...this.state, fixed: true, top: null, bottom: null };
  }

  public render() {
    const { sectionIds, currentSectionId, sectionNamesMap, navTitle, activeColor, onClick } = this.props;
    const { positioned, fixed, top, bottom } = this.state;
    return (
      <Base hidden={!positioned} fixed={fixed} top={top} bottom={bottom} innerRef={el => (this.base = el)}>
        <Header>{navTitle}</Header>
        <div>
          {sectionIds.map(sectionId => (
            <SectionAsideNavItem
              key={sectionId}
              sectionId={sectionId}
              displayName={sectionNamesMap[sectionId]}
              active={sectionId === currentSectionId}
              activeColor={activeColor!}
              onClick={onClick}
            >
              {sectionNamesMap[sectionId]}
            </SectionAsideNavItem>
          ))}
        </div>
      </Base>
    );
  }
}

const MAIN_COLUMN_WIDTH = 980;
const OFFSET_FROM_MAIN_COLUMN = 140;

interface BaseStyleProps {
  hidden: boolean;
  fixed: boolean;
  top: number | null;
  bottom: number | null;
}

const Base = styled.div`
  display: none;
  @media ${media.greaterThan(MAIN_COLUMN_WIDTH + OFFSET_FROM_MAIN_COLUMN * 2)} {
    display: ${(props: BaseStyleProps) => (props.hidden ? "none" : "block")};
    z-index: 10;
    min-width: 90px;
    padding: 50px 0;
    left: calc((100% - ${MAIN_COLUMN_WIDTH}px) / 2 - ${OFFSET_FROM_MAIN_COLUMN}px);
    ${(props: BaseStyleProps) =>
      props.fixed
        ? css`
            position: fixed;
            top: 50%;
            transform: translateY(-50%);
          `
        : css`
            position: absolute;
            top: ${props.top === null ? "unset" : `${props.top}px`};
            bottom: ${props.bottom === null ? "unset" : `${props.bottom}px`};
          `};
  }
`;

const Header = styled.div`
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 17px;
`;

const SectionAsideNavWithScreenSize: React.SFC<OwnProps> = props => {
  return <WithScreenSize>{({ screenSize }) => <SectionAsideNav {...props} screenSize={screenSize} />}</WithScreenSize>;
};

export default SectionAsideNavWithScreenSize;



// WEBPACK FOOTER //
// ./frontend/src/companies/components/organisms/SectionAsideNav.tsx